import React, { FC, useEffect, useMemo, useState } from "react";
import Button, { BUTTON_VARIANTS } from "components/shared/Button/Button";
import Form from "./Form/Form";
import styles from "./EmployeeCard.module.sass";
import { Employee, RootObject } from "pages/EmployeePage/EmployeeCard/model";
import { useDispatch } from "react-redux";
import { useAppSelector } from "toolkit/hooks";
import { getOrganizationsDirectory } from "toolkit/directories/directories.thunks";
import useDebounce from "hooks/useDebounce";
import NoCard from "pages/EmployeePage/UserCard/NoCard/NoCard";
import StepInfo from "pages/StepPages/components/StepInfo";
import { replacePhone, snackWrapper } from "helpers";
import { employeesAPI } from "libs/apiClient/Employees";
import { userAPI } from "libs/apiClient/User";
import { employeeAPI } from "libs/apiClient/Employee";

const initial: Employee = {
	commissionsMemeberhip: [],
	dismissalDate: "",
	email: "",
	employmentDate: "",
	firstName: "",
	lastName: "",
	middleName: "",
	phone: "",
	tabNumber: "",
};

interface EmployeeCardProps {
	refresh: () => void;
	disabled: boolean;
}

const EmployeeCard: FC<EmployeeCardProps> = ({ refresh, disabled }) => {
	const [disabledForm, setDisabledForm] = useState<boolean>(true);

	const dispatch = useDispatch();
	const organizationsDirectory = useAppSelector((state) => state.directories.organizationsDirectory);
	useEffect(() => {
		if (!organizationsDirectory.length) {
			// @ts-ignore
			dispatch(getOrganizationsDirectory());
		}
		//eslint-disable-next-line
	}, []);
	const selectedEmployee: RootObject = useAppSelector((state) => state.employess.selectedEmployee);

	const options = useMemo(() => {
		let result: any = {};
		if (selectedEmployee && selectedEmployee.dropValues) {
			let selectedEmployeeKey: keyof typeof selectedEmployee.dropValues;
			for (selectedEmployeeKey in selectedEmployee.dropValues) {
				const item = selectedEmployee.dropValues[selectedEmployeeKey];
				result[selectedEmployeeKey] = Object.values(item).map((_: any, index: number) => ({ id: index + 1, name: _ }));
			}
		}
		return result;
	}, [selectedEmployee]);

	const [state, setState] = useState<typeof selectedEmployee.employee>(initial);

	const changeField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setState((prevState: any) => ({ ...prevState, [name]: value }));
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [supervisorOptions, setSupervisorOptions] = useState<{ fullName: string; id: number }[]>([]);

	const selectField = (name: keyof typeof state, e: any) => {
		setState((prevState: any) => ({ ...prevState, [name]: e }));
	};

	useEffect(() => {
		if (selectedEmployee && selectedEmployee.employee) {
			setState({
				...selectedEmployee.employee,
				phone: replacePhone(selectedEmployee.employee.phone, "to_front"),
			});
		}
		if (selectedEmployee && selectedEmployee.employee && selectedEmployee.employee.supervisor) {
			const { lastName, firstName, middleName } = selectedEmployee.employee.supervisor;
			setSearch(`${lastName} ${firstName} ${middleName}`);
		}
	}, [selectedEmployee]);

	const [search, setSearch] = useState<string>("");

	const query = useDebounce(search as string, 400);

	useEffect(() => {
		if (query) {
			employeesAPI.shortList({ page: 1, num: 9999999, query }).then(({ data }) => setSupervisorOptions(data));
			// $api.get("/employees/short?page=1&num=", { params: { query } }).then((r) => );
		} else {
			setSupervisorOptions([]);
			setState((prevState) => ({ ...prevState, supervisor: null }));
		}
	}, [query]);

	const unbindUser = () => {
		userAPI
			.profileBindDelete({ userid: selectedEmployee.user.id })
			.then(() => {
				refresh();
				snackWrapper("success", "Отвязка прошла успешно");
			})
			.catch(() => {
				snackWrapper("error", "Не удалось отвязать сотрудника и пользователя");
			});
		// $api
		// 	.delete(`/user/profile/bind?userid=${selectedEmployee?.user.id}`)
		// 	.then(() => {
		// 		refresh();
		// 		snackWrapper("success", "Отвязка прошла успешно");
		// 	})
		// 	.catch(() => {
		// 		snackWrapper("error", "Не удалось отвязать сотрудника и пользователя");
		// 	});
	};

	const onSave = () => {
		const {
			commissionsMemeberhip,
			organization,
			department,
			relationship,
			position,
			supervisor,
			status,
			user,
			id,
			email,
			phone,
			dismissalDate,
			...rest
		} = state;
		const payload = {
			...rest,
			// ...(department && { department: department }),
			...(email && { email }),
			...(phone && { phone: replacePhone(phone, "to_server") }),
			...(dismissalDate && { dismissalDate }),
			...(supervisor && { supervisorId: supervisor.id }),
			department: department || "",
			position: position || "",
			orgId: organization!.id,
			orgRelationship: relationship!.id,
			statusId: status!.id,
		};
		employeeAPI
			.employeeUpdate(payload, String(id))
			.then(() => {
				setDisabledForm(true);
			})
			.catch((e: any) => {
				if (e.response.data.toLowerCase() === "email is not valid") {
					snackWrapper("error", "Не валидный email");
				}
			});
		// $api
		// 	.put(`/employee/${id}`, { ...payload })
		// 	.then(() => {
		// 		setDisabledForm(true);
		// 	})
		// 	.catch((e: any) => {
		// 		if (e.response.data.toLowerCase() === "email is not valid") {
		// 			Snackbar({
		// 				type: "error",
		// 				title: "Не валидный email",
		// 				position: "top-center",
		// 				delay: 3000,
		// 				factor: "light",
		// 			});
		// 		}
		// 	});
	};

	return (
		<div className={styles.employeeCard}>
			<div className="step-header">
				<StepInfo info="">
					<div className={styles.title}>Карточка сотрудника</div>
				</StepInfo>
				<div className="df">
					{selectedEmployee?.employee && selectedEmployee?.user && (
						<Button variant={BUTTON_VARIANTS.OUTLINED} className={styles.button} onClick={unbindUser}>
							Открепить
						</Button>
					)}

					{selectedEmployee?.employee &&
						(!disabledForm ? (
							<Button className={styles.button} onClick={onSave}>
								Сохранить
							</Button>
						) : (
							<Button
								disabled={disabled}
								className={styles.button}
								variant={
									selectedEmployee?.employee && selectedEmployee?.user
										? BUTTON_VARIANTS.CONTAINED
										: BUTTON_VARIANTS.OUTLINED
								}
								onClick={() => setDisabledForm(false)}>
								Изменить информацию
							</Button>
						))}
				</div>
			</div>

			{selectedEmployee?.employee ? (
				<div className={styles.content}>
					<Form
						options={options}
						organizationsDirectory={organizationsDirectory}
						selectField={selectField}
						setSupervisorOptions={setSupervisorOptions}
						setSearch={setSearch}
						state={state}
						changeField={changeField}
						search={search}
						disabledForm={disabledForm}
					/>
				</div>
			) : (
				<NoCard />
			)}
		</div>
	);
};

export default EmployeeCard;
